import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ObjectState } from '../interfaces/entity';
import { ISubjectTypeList, SubjectType } from '../models/subject-type';
import { BaseRestService } from './base-rest.service';
import { DataFieldService } from './data-field.service';
import { NavigationHelper } from './navigation-helper';

@Injectable({ providedIn: 'root' })
export class SubjectTypeService extends BaseRestService<SubjectType, ISubjectTypeList> implements OnDestroy {

  onDataFieldsChanged: BehaviorSubject<any> = new BehaviorSubject([]);

  private dataFieldsChanged$: Subscription;

  constructor(http: HttpClient, private dataFieldService: DataFieldService, protected router: Router) {
    super(SubjectTypeService, http, 'api/subjecttype', 'subjecttype', router);

    // enable caching
    this.cacheEnabled = true;

    this.dataFieldsChanged$ = this.dataFieldService.onDataFieldsChanged.subscribe((response) => {
      if (this.currentItem && (response.parent.uid === this.currentItem.uid)) {
        this.currentItem.dataFields = response.dataFields;
        this.onDataFieldsChanged.next(this.currentItem.dataFields);
      }
    });
  }

  ngOnDestroy() {
    this.dataFieldsChanged$.unsubscribe();
  }

  getAll() {
    this.dataFieldService.getAvailableDataTypes();
    return super.getAll();
  }

  // get first subject type that matches name.  Will 404 if id not found
  getByName(name: string): Observable<SubjectType> {
    return this.getAllData().pipe(map((subjectTypes) => {
      return subjectTypes.find((x) => x.name.toLowerCase() === name.toLowerCase());
    }));
  }

  afterSave(entity) {
    // make sure objectState is reset on datafields
    entity.dataFields.forEach((x) => {
      if (x.objectState !== ObjectState.Deleted)
      {
        x.objectState = ObjectState.Unchanged;
      }
    });

    entity.dataFields = entity.dataFields.filter((x) => x.objectState !== ObjectState.Deleted);
    this.onItemChanged.next(entity);

    // get an up to date list of available subject types...
    this.reloadNavigation();
  }

  reloadNavigation()
  {

    this.getAllData().pipe(take(1)).subscribe((subjectTypes) => {
      // set the relevant property in the navigation helper
      NavigationHelper.subjectTypes = subjectTypes.filter((x) => !x.hideRecord);
      // call updateNavigation to ensure the navbar is updated to reflect any changes
      NavigationHelper.updateNavigation();
    });
  }

  public getName(idValue: number): any {
    const item = this.getCache().find((i) => idValue === i.id );
    if (item != null) {
      return item.name;
    }
    else {
      return idValue;
    }
  }

  save(entity: SubjectType, successMessage?: string) {
    // make sure that any new data fields do not have id set otherwise they won't insert into the database properly
    entity.dataFields.forEach((x) => {
      if (!x.objectState) {
        x.objectState = ObjectState.Unchanged;
      }
      if (x.objectState === ObjectState.Added) {
        x.id = null;
      }
    });
    return super.save(entity, successMessage);
  }

  delete(entity: SubjectType|string|number, resetCache?: boolean ): Observable<SubjectType>
  {
    return super.delete(entity, resetCache);
  }
}
